import React, { useState } from 'react';
import { Button, Space, Spin, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DeepAnalysis, Feedback, RadarDetail, errorMessage, fetchItem, fetchTrend, requestAnalyze, sendFeedback } from './api';
import { ANALYSIS_LABELS, KIND_LABEL, SOURCE_COLOR, formatSourceTime, kstStamp, newsBadge, venueName } from './helpers';

const { Text, Title } = Typography;

const ANALYSIS_KEYS: (keyof DeepAnalysis & ('issue' | 'implication' | 'angle' | 'reporting'))[] = [
  'issue',
  'implication',
  'angle',
  'reporting',
];

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '110px 1fr',
  gap: '6px 10px',
  fontSize: 13,
  lineHeight: 1.6,
};

function Sparkline({ points, label }: { points: { period: string; ratio: number }[]; label: string }) {
  if (points.length < 2) return null;
  const w = 180;
  const h = 36;
  const max = Math.max(...points.map((p) => p.ratio), 1);
  const d = points.map((p, i) => `${(i / (points.length - 1)) * w},${h - (p.ratio / max) * (h - 2) - 1}`).join(' ');
  return (
    <svg width={w} height={h} role="img" aria-label={label}>
      <polyline points={d} fill="none" stroke="#1677ff" strokeWidth={1.5} />
    </svg>
  );
}

interface Props {
  itemId: string;
  onBack?: () => void;
}

const PitchDetail: React.FC<Props> = ({ itemId, onBack }) => {
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const { data: item, isLoading } = useQuery({
    queryKey: ['radarItem', itemId],
    queryFn: () => fetchItem(itemId),
    refetchOnWindowFocus: false,
  });
  const { data: trend } = useQuery({
    queryKey: ['radarTrend', itemId],
    queryFn: () => fetchTrend(itemId),
    refetchOnWindowFocus: false,
    staleTime: 60 * 60 * 1000,
  });

  const analyze = useMutation({
    mutationFn: () => requestAnalyze(itemId),
    onSuccess: ({ analysis }) => {
      queryClient.setQueryData<RadarDetail>(['radarItem', itemId], (prev) => (prev ? { ...prev, analysis, hasAnalysis: true } : prev));
      queryClient.invalidateQueries({ queryKey: ['radarItems'] });
    },
    onError: (err) => message.warning(errorMessage(err, '분석에 실패했습니다')),
  });

  const vote = useMutation({
    mutationFn: (value: 'up' | 'down' | 'used') => sendFeedback(itemId, value),
    onSuccess: (r) => setFeedback(r.feedback),
    onError: (err) => message.warning(errorMessage(err)),
  });

  if (isLoading || !item) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const counts: Feedback = feedback ?? { ...item.feedback, used: item.feedback?.used ?? 0 };
  const labels = ANALYSIS_LABELS[item.radar];
  const badge = newsBadge(item.news);
  const trendLabel = trend?.timeUnit === 'week' ? '최근 8주 네이버 검색량 추이' : '최근 30일 네이버 검색량 추이';

  return (
    <div style={{ padding: '4px 4px 16px' }}>
      {onBack && (
        <Button type="link" onClick={onBack} style={{ padding: 0, marginBottom: 8 }}>
          ← 목록
        </Button>
      )}
      <Title level={5} style={{ marginTop: 0, wordBreak: 'keep-all' }}>
        {item.title}
      </Title>
      <Space size={6} wrap style={{ marginBottom: 12 }}>
        {badge && <Text style={{ fontSize: 12, color: badge.color }}>{badge.text}</Text>}
        {item.news.status === 'unknown' && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            보도 여부 확인 못 함
          </Text>
        )}
        {item.kind && item.radar === 'community' && <Tag>{KIND_LABEL[item.kind] ?? item.kind}</Tag>}
        {item.radar === 'life' && <Text style={{ fontSize: 12 }}>{`카페·블로그 ${item.venueCount}곳`}</Text>}
        {item.trend && <Text style={{ fontSize: 12 }}>{`이번 주 ${item.trend.thisWeek} (지난주 ${item.trend.lastWeek})`}</Text>}
        {item.searchSurge && <Text style={{ fontSize: 12, color: '#d46b08' }}>🔍검색 급증</Text>}
        {item.tracking?.firstReportedAt && (
          <Text style={{ fontSize: 12, color: '#1677ff' }}>{`${item.tracking.leadDays}일 먼저 잡음`}</Text>
        )}
        {item.beats.map((b) => (
          <Tag key={b} color="blue" style={{ fontSize: 11 }}>
            {b}
          </Tag>
        ))}
      </Space>

      {item.analysis ? (
        <div style={rowStyle}>
          {ANALYSIS_KEYS.map((key) => (
            <React.Fragment key={key}>
              <Text type="secondary">{labels[key]}</Text>
              <span>{String(item.analysis?.[key] ?? '')}</span>
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div>
          <p style={{ fontSize: 13, margin: '0 0 6px' }}>{item.summary}</p>
          {item.angle && (
            <p style={{ fontSize: 12, margin: '0 0 10px', color: '#595959' }}>
              <Text type="secondary">각도 힌트 </Text>
              {item.angle}
            </p>
          )}
          <Button type="primary" ghost size="small" loading={analyze.isPending} onClick={() => analyze.mutate()}>
            자세히 분석
          </Button>
        </div>
      )}

      <div style={{ ...rowStyle, marginTop: 16 }}>
        <Text type="secondary">{`근거 글 ${item.sources.length}`}</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {item.sources.map((s) => (
            <div key={s.link} style={{ fontSize: 13 }}>
              <span style={{ color: SOURCE_COLOR[s.source], fontWeight: 600, marginRight: 6 }}>{venueName(s)}</span>
              <a href={s.link} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'keep-all' }}>
                {s.title}
              </a>
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                {formatSourceTime(s)}
              </Text>
              {s.summary && s.summary !== item.summary && (
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{s.summary}</div>
              )}
            </div>
          ))}
        </div>

        {item.news.articles?.length > 0 && (
          <>
            <Text type="secondary">관련 기사</Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {item.news.articles.map((a) => (
                <div key={a.link} style={{ fontSize: 13 }}>
                  <a href={a.link} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'keep-all' }}>
                    {a.title}
                  </a>
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                    {kstStamp(a.pubDate)}
                  </Text>
                </div>
              ))}
            </div>
          </>
        )}

        {trend && trend.points.length > 1 && (
          <>
            <Text type="secondary">검색량</Text>
            <Sparkline points={trend.points} label={trendLabel} />
          </>
        )}
      </div>

      <Space style={{ marginTop: 16 }} wrap>
        <Button size="small" onClick={() => vote.mutate('up')} disabled={vote.isPending}>
          {`쓸만함 👍 ${counts.up}`}
        </Button>
        <Button size="small" onClick={() => vote.mutate('down')} disabled={vote.isPending}>
          {`별로 👎 ${counts.down}`}
        </Button>
        <Button size="small" type="primary" ghost onClick={() => vote.mutate('used')} disabled={vote.isPending}>
          {`발제에 씀 ✍️ ${counts.used}`}
        </Button>
      </Space>
    </div>
  );
};

export default PitchDetail;
